// ============================================================
// App.jsx — Main Application Entry Point
// ============================================================
// Wraps in AuthProvider, handles routing, role-based nav

import React, { useState } from "react";
import { AuthProvider, useAuth } from "./contexts/AuthContext";

// Pages — Customer
import HomePage from "./pages/HomePage";
import MenuPage from "./pages/MenuPage";
import BookingPage from "./pages/BookingPage";
import LoyaltyPage from "./pages/LoyaltyPage";
import AboutPage from "./pages/AboutPage";
import AuthPage from "./pages/AuthPage";
import ProfilePage from "./pages/ProfilePage";
import MyBookingsPage from "./pages/MyBookingsPage";

// Pages — Admin
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminProducts from "./pages/admin/AdminProducts";
import AdminBookings from "./pages/admin/AdminBookings";
import AdminCustomers from "./pages/admin/AdminCustomers";
import AdminLoyalty from "./pages/admin/AdminLoyalty";
import AdminReviews from "./pages/admin/AdminReviews";
import AdminCampaigns from "./pages/admin/AdminCampaigns";
import AdminFinancials from "./pages/admin/AdminFinancials";
import AdminSettings from "./pages/admin/AdminSettings";

// Components
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import Toast from "./components/Toast";
import LoadingSpinner from "./components/LoadingSpinner";

function AppContent() {
  const { user, role, loading, isAdmin, signOut } = useAuth();
  const [currentView, setCurrentView] = useState("home");
  const [toast, setToast] = useState(null);
  const [preselectedProduct, setPreselectedProduct] = useState(null);
  const [preselectedSize, setPreselectedSize] = useState(null);
  const [cart, setCart] = useState([]);
  const [bookingForm, setBookingForm] = useState({
    pickupDate: "",
    pickupTime: "",
    deliveryMethod: "pickup",
    deliveryAddress: "",
    promoCode: "",
  });

  const navigateToBook = (product, size) => {
    setPreselectedProduct(product || null);
    setPreselectedSize(size || null);
    setCurrentView("book");
  };

  const showToast = (msg, type = "info") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  if (loading) return <LoadingSpinner />;

  // Route to auth page if accessing protected routes while logged out
  const protectedViews = [
    "book", "loyalty", "profile", "my-bookings",
    "admin-dashboard", "admin-products", "admin-bookings",
    "admin-customers", "admin-loyalty", "admin-reviews",
    "admin-campaigns", "admin-financials", "admin-settings",
  ];

  if (!user && protectedViews.includes(currentView)) {
    return (
      <>
        <Navbar currentView={currentView} setCurrentView={setCurrentView} />
        <div className="page">
          <AuthPage onSuccess={() => setCurrentView("home")} showToast={showToast} />
        </div>
        <Footer setCurrentView={setCurrentView} />
      </>
    );
  }

  // Block non-admin from admin views
  const adminViews = currentView.startsWith("admin-");
  if (adminViews && !isAdmin) {
    setCurrentView("home");
  }

  const renderView = () => {
    switch (currentView) {
      // Customer pages
      case "home":        return <HomePage setCurrentView={setCurrentView} showToast={showToast} navigateToBook={navigateToBook} />;
      case "menu":        return <MenuPage showToast={showToast} setCurrentView={setCurrentView} navigateToBook={navigateToBook} />;
      case "book":        return <BookingPage showToast={showToast} preselectedProduct={preselectedProduct} preselectedSize={preselectedSize} cart={cart} setCart={setCart} bookingForm={bookingForm} setBookingForm={setBookingForm} />;
      case "loyalty":     return <LoyaltyPage />;
      case "about":       return <AboutPage />;
      case "auth":        return <AuthPage onSuccess={() => setCurrentView("home")} showToast={showToast} />;
      case "profile":     return <ProfilePage showToast={showToast} />;
      case "my-bookings": return <MyBookingsPage showToast={showToast} />;

      // Admin pages
      case "admin-dashboard":  return <AdminDashboard />;
      case "admin-products":   return <AdminProducts showToast={showToast} />;
      case "admin-bookings":   return <AdminBookings showToast={showToast} />;
      case "admin-customers":  return <AdminCustomers showToast={showToast} />;
      case "admin-loyalty":    return <AdminLoyalty showToast={showToast} />;
      case "admin-reviews":    return <AdminReviews showToast={showToast} />;
      case "admin-campaigns":  return <AdminCampaigns showToast={showToast} />;
      case "admin-financials": return <AdminFinancials />;
      case "admin-settings":   return <AdminSettings showToast={showToast} />;

      default: return <HomePage setCurrentView={setCurrentView} showToast={showToast} />;
    }
  };

  return (
    <div className="app-container">
      <Toast toast={toast} />
      <Navbar currentView={currentView} setCurrentView={setCurrentView} />
      <div className="page">
        {renderView()}
      </div>
      <Footer setCurrentView={setCurrentView} />
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}